// Sensor3DView.jsx - full-room GP heatmap viewer
// Requires: @react-three/fiber, @react-three/drei, three, axios

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Html, Line } from '@react-three/drei';
import axios from 'axios';
import * as THREE from 'three';

// ─── API ─────────────────────────────────────────────────────────────────────

const BASE_URL = process.env.REACT_APP_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use(cfg => {
  const tok = localStorage.getItem('accessToken');
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});
api.interceptors.response.use(
  r => r,
  e => { if (e?.response?.status === 401) window.location.href = '/login'; return Promise.reject(e); }
);

// ─── SENSOR CONFIG ────────────────────────────────────────────────────────────
// In metres, from config: [roomX, roomY, heightZ]
// Three.js is Y-up: roomX→threeX, heightZ→threeY, roomY→threeZ

const SENSOR_POSITIONS = {
  S0:  [1.6,  2.25, 0.84],
  S1:  [0.1,  0.6,  1.7 ],
  S2:  [0.1,  0.6,  0.4 ],
  S3:  [0.1,  2.0,  1.1 ],
  S4:  [0.1,  0.6,  1.1 ],
  S5:  [1.4,  0.65, 1.9 ],
  S6:  [1.6,  2.25, 0.18],
  S7:  [1.1,  2.63, 1.9 ],
  S8:  [0.7,  3.4,  0.18],
  S9:  [0.7,  3.4,  0.84],
  S10: [1.0,  3.8,  2.03],
  S11: [2.5,  3.6,  0.5 ],
  S12: [2.5,  3.4,  1.2 ],
  S13: [2.5,  3.5,  1.8 ],
  S14: [1.8,  4.7,  1.46],
  S15: [1.4,  3.0,  1.9]
};

// [roomX, roomY, roomZ(height)] → Three.js [x, y, z]
const toThree = ([rx, ry, rz]) => [-rx, rz, ry];

const DEFAULT_BOUNDS = { x: [-0.05, 2.7], y: [-0.05, 5.0], z: [0, 2.2] };

// ─── COLOUR ───────────────────────────────────────────────────────────────────

function valueToColor(value, min, max, mode) {
  const t = Math.min(Math.max((value - min) / (max - min || 1), 0), 1);
  const c = new THREE.Color();
  if (mode === 'temp') {
    c.setHSL((1 - t) * 0.67, 1.0, 0.5);
  } else {
    c.setHSL(0.58, 0.85, 0.2 + t * 0.55);
  }
  return c;
}

// ─── ROOM WIREFRAME ───────────────────────────────────────────────────────────

function RoomBox({ bounds }) {
  const b = bounds || DEFAULT_BOUNDS;
  const w  = b.x[1] - b.x[0];
  const d  = b.y[1] - b.y[0];
  const h  = b.z[1] - b.z[0];
  
  // NEGATE the X center to match the toThree mapping
  const cx = -((b.x[0] + b.x[1]) / 2); 
  const cy = (b.z[0] + b.z[1]) / 2;
  const cz = (b.y[0] + b.y[1]) / 2;

  return (
    <group position={[cx, cy, cz]}>
      <mesh>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color="#3366aa" transparent opacity={0.03} side={THREE.BackSide} />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(w, h, d)]} />
        <lineBasicMaterial color="#3a5f99" transparent opacity={0.28} />
      </lineSegments>
    </group>
  );
};

// ─── VOXEL GRID ───────────────────────────────────────────────────────────────

function VoxelGrid({ predictions, mode, minVal, maxVal, opacity, bounds }) {
  const voxels = useMemo(() => {
    if (!predictions.length) return [];
    const uniq = axis => [...new Set(predictions.map(p => p[axis]))].sort((a, b) => a - b);
    const gap  = arr => arr.length > 1 ? arr[1] - arr[0] : 0.3;

    const dx = gap(uniq('x'));
    const dy = gap(uniq('y'));
    const dz = gap(uniq('z'));

    return predictions.map(p => ({
      pos:   toThree([p.x, p.y, p.z]), // This already negates the X
      size:  [dx, dz, dy],
      color: valueToColor(p.prediction, minVal, maxVal, mode),
    }));
  }, [predictions, mode, minVal, maxVal]);

  if (!voxels.length) return null;

  return (
    <group position={[0, 0, 0]}> 
      {voxels.map((v, i) => (
        <mesh key={i} position={v.pos}>
          <boxGeometry args={v.size} />
          <meshStandardMaterial
            color={v.color}
            transparent
            opacity={opacity}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}

// ─── SENSOR SPHERE ────────────────────────────────────────────────────────────



// Inside SensorSphere Component:
function SensorSphere({ name, roomPos, temp, humidity }) {
  const meshRef = useRef();
  const pos = useMemo(() => toThree(roomPos), [roomPos]);

  useFrame(({ clock }) => {
    if (meshRef.current) {
      meshRef.current.scale.setScalar(1 + Math.sin(clock.elapsedTime * 2 + roomPos[0]) * 0.08);
    }
  });

  const labelOffset = 0.25; // How far above the sphere the label should sit

  return (
    <group position={pos}>
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.06, 16, 16]} />
        <meshStandardMaterial color="#ff3333" emissive="#ff0000" />
      </mesh>
      
      {/* leader line connecting sphere to label */}
      <Line
        points={[[0, 0, 0], [0, labelOffset * 0.9, 0]]} // Slightly shorter than label pos
        color="rgba(0, 255, 204, 0.4)"
        lineWidth={0.5}
      />

      <Html 
        distanceFactor={6}       // Labels scale more naturally with depth
        position={[0, labelOffset, 0]} 
        center
        occlude={false}         // We keep this to ensure text is always readable
        style={{
          pointerEvents: 'none',
          userSelect: 'none',
          transform: 'translate3d(-50%, -50%, 0)'
        }}
      >
        <div style={{
          background: 'rgba(0, 0, 0, 0.8)',
          color: '#00ffcc',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '11px',
          fontWeight: '900',
          whiteSpace: 'nowrap',
          border: '1px solid rgba(0, 255, 204, 0.5)',
          boxShadow: '0 0 10px rgba(0,255,204,0.3)',
          fontFamily: 'monospace'
        }}>
          {name}: {temp ? `${temp.toFixed(1)}°C` : 'N/A'} - {humidity ? `${humidity.toFixed(1)}%` : 'N/A'}
        </div>
      </Html>
    </group>
  );
}

// ─── FLOOR GRID ───────────────────────────────────────────────────────────────

function FloorGrid({ bounds }) {
  const b    = bounds || DEFAULT_BOUNDS;
  const cx   = -((b.x[0] + b.x[1]) / 2); // NEGATE this
  const cz   = (b.y[0] + b.y[1]) / 2;
  const size = Math.max(b.x[1] - b.x[0], b.y[1] - b.y[0]) + 0.6;
  return <gridHelper args={[size, 14, '#162030', '#0e1820']} position={[cx, b.z[0], cz]} />;
}

// ─── LEGEND ───────────────────────────────────────────────────────────────────

function Legend({ mode, minVal, maxVal, stats }) {
  const stops = 7;
  const unit = mode === 'temp' ? '°C' : '%';
  
  const grad = Array.from({ length: stops }, (_, i) => {
    const v = minVal + (i / (stops - 1)) * (maxVal - minVal);
    return `#${valueToColor(v, minVal, maxVal, mode).getHexString()}`;
  });

  return (
    <div style={{
      position: 'absolute', bottom: 22, left: 18,
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 'var(--r-lg)', padding: '12px 16px', background: 'rgba(4,8,16,0.84)',
      color: '#bbc', fontSize: 11,
      backdropFilter: 'blur(8px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
    }}>
      <div style={{ fontWeight: 600, marginBottom: 8, color: '#dde', letterSpacing: '.03em' }}>
        {mode === 'temp' ? '🌡 Temperature' : '💧 Humidity'}
      </div>
      
      {/* Gradient Bar */}
      <div style={{
        height: 8, width: 180, borderRadius: 4,
        background: `linear-gradient(to right, ${grad.join(',')})`,
        marginBottom: 6,
      }} />
      
      {/* Min/Max Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#778', fontSize: 10, marginBottom: 12 }}>
        <span>{minVal.toFixed(1)}{unit}</span>
        <span>{maxVal.toFixed(1)}{unit}</span>
      </div>

      {/* --- STATS SECTION --- */}
      <div style={{ 
        borderTop: '1px solid rgba(255,255,255,0.05)', 
        paddingTop: 8, 
        display: 'flex', 
        gap: '15px',
        fontSize: '10px'
      }}>
        <div>
          <span style={{ color: '#556', marginRight: 4 }}>AVG</span>
          <span style={{ color: '#aac' }}>{stats.mean.toFixed(1)}{unit}</span>
        </div>
        <div>
          <span style={{ color: '#556', marginRight: 4 }}>STDEV (σ)</span>
          <span style={{ color: '#00ffcc' }}>±{stats.stdev.toFixed(2)}{unit}</span>
        </div>
      </div>
    </div>
  );
}

// ─── TOOLBAR ─────────────────────────────────────────────────────────────────

function Toolbar({ mode, setMode, loading, lastUpdated, opacity, setOpacity }) {
  const btn = (id, label) => (
    <button onClick={() => setMode(id)} style={{
      padding: '6px 14px', borderRadius: 6, border: 'none',
      background: mode === id ? '#2558b0' : 'rgba(255,255,255,0.06)',
      color: mode === id ? '#fff' : '#778899',
      fontSize: 12, fontWeight: 500, cursor: 'pointer',
      transition: 'all .15s',
    }}>{label}</button>
  );
  return (
    <div style={{
      position: 'absolute', top: 14, left: 14, zIndex: 10,
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'rgba(4,8,16,0.84)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 'var(--r-lg)', padding: '7px 12px',
      backdropFilter: 'blur(8px)',
    }}>
      {btn('temp',     '🌡 Temperature')}
      {btn('humidity', '💧 Humidity')}
      <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.07)', margin: '0 4px' }} />
      <label style={{ color: 'rgb(149, 149, 161)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
        Opacity
        <input type="range" min={10} max={85} value={Math.round(opacity * 100)}
          onChange={e => setOpacity(Number(e.target.value) / 100)}
          style={{ width: 65, accentColor: '#2558b0' }} />
      </label>
      {loading      && <span style={{ color: '#2d6bc4', fontSize: 11 }}>updating…</span>}
      {lastUpdated && !loading && (
        <span style={{ color: '#aab', fontSize: 10 }}>{lastUpdated.toLocaleTimeString()}</span>
      )}
    </div>
  );
}

// ─── FIT QUALITY BADGE ────────────────────────────────────────────────────────

function FitQualityInfo({ meta, mode }) {
  if (!meta) return null;
  const { looCvRmse, sensorCount, droppedSensors } = meta[mode] || {};
  const unit    = mode === 'temp' ? '°C' : '%';
  const quality = looCvRmse == null ? null
    : looCvRmse < 0.4 ? 'good' : looCvRmse < 1.0 ? 'ok' : 'poor';
  const qColor  = { good: '#44cc88', ok: '#ddaa33', poor: '#cc4444' }[quality] ?? '#445566';

  return (
    <div style={{
      position: 'absolute', top: 14, right: 14, zIndex: 10,
      background: 'rgba(4,8,16,0.84)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 'var(--r-lg)', padding: '8px 14px',
      color: '#bbc', fontSize: 11,
      backdropFilter: 'blur(8px)',
    }}>
      <div style={{ color: '#a4b6c9', marginBottom: 4, fontSize: 10 }}>interpolation quality</div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        {looCvRmse != null && (
          <div>LOO RMSE <span style={{ color: qColor, fontWeight: 600 }}>
            ±{looCvRmse.toFixed(2)}{unit}
          </span></div>
        )}
        {sensorCount != null && <div style={{ color: '#aeb8c2' }}>{sensorCount} sensors</div>}
        {quality && (
          <div style={{
            background: qColor + '22', color: qColor,
            borderRadius: 4, padding: '1px 7px', fontWeight: 600, fontSize: 10,
          }}>{quality}</div>
        )}
      </div>
      {droppedSensors?.length > 0 && (
        <div style={{ color: '#cc4444', fontSize: 9, marginTop: 4 }}>
          ⚠ {droppedSensors.length} sensor(s) out of bounds
        </div>
      )}
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────

const Sensor3DView = () => {
  const [sensorData,  setSensorData]  = useState({});
  const [predictions, setPredictions] = useState({ temp: [], humidity: [] });
  const [bounds,      setBounds]      = useState(DEFAULT_BOUNDS);
  const [meta,        setMeta]        = useState(null);
  const [mode,        setMode]        = useState('temp');
  const [loading,     setLoading]     = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [opacity,     setOpacity]     = useState(0.4);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [sensorRes, tempRes, humRes] = await Promise.all([
          api.get('/sensor-data/verbose', { params: { limit: 1 } }),
          api.post('/sensor-data/rbfInterpolate/temp', {}),
          api.post('/sensor-data/rbfInterpolate/humidity', {}),
        ]);

        setSensorData(sensorRes.data[0] || {});
        setPredictions({
          temp:     tempRes.data.predictions || [],
          humidity: humRes.data.predictions  || [],
        });
        if (tempRes.data.bounds) setBounds(tempRes.data.bounds);
        setMeta({
          temp:     { looCvRmse: tempRes.data.looCvRmse, sensorCount: tempRes.data.sensorCount, droppedSensors: tempRes.data.droppedSensors },
          humidity: { looCvRmse: humRes.data.looCvRmse,  sensorCount: humRes.data.sensorCount,  droppedSensors: humRes.data.droppedSensors  },
        });
        setLastUpdated(new Date());
      } catch {
        // fetch error - data remains stale until next poll
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    const id = setInterval(fetchAll, 30_000);
    return () => clearInterval(id);
  }, []);

const activePreds = predictions[mode] || [];
const predVals = activePreds.map(p => p.prediction).filter(Number.isFinite);

const rawMin = predVals.length ? Math.min(...predVals) : 0;
const rawMax = predVals.length ? Math.max(...predVals) : 1;

// --- NEW STATS CALCULATION ---
const stats = useMemo(() => {
  if (!predVals.length) return { mean: 0, stdev: 0, variance: 0 };
  const n = predVals.length;
  const mean = predVals.reduce((a, b) => a + b, 0) / n;
  const variance = predVals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / n;
  const stdev = Math.sqrt(variance);
  return { mean, stdev, variance };
}, [predVals]);
// -----------------------------

const pad = (rawMax - rawMin) * 0.04;

// Inside Sensor3DView component:
const camTarget = [
  -((bounds.x[0] + bounds.x[1]) / 2), // Negate the center X
  (bounds.z[0] + bounds.z[1]) / 2,
  (bounds.y[0] + bounds.y[1]) / 2,
];

  const sensorReadings = useMemo(() => {
    const out = {};
    for (let i = 0; i <= 15; i++) {
      const k = `sensor${i}`;
      if (sensorData[k] && typeof sensorData[k] === 'object') out[`S${i}`] = sensorData[k];
    }
    if (Number.isFinite(sensorData.CO2temp)) {
      out['S16'] = { temp: sensorData.CO2temp, humidity: sensorData.CO2humid };
    }
    return out;
  }, [sensorData]);

  return (
    <div className="dashboard-container">
    <div style={{
      position: 'relative', width: '100%', height: '80vh',
      borderRadius: 'var(--r-2xl)', overflow: 'hidden',
      fontFamily: 'system-ui, sans-serif',
    }}>
      <Toolbar
        mode={mode} setMode={setMode}
        loading={loading} lastUpdated={lastUpdated}
        opacity={opacity} setOpacity={setOpacity}
      />
      <FitQualityInfo meta={meta} mode={mode} />

      <Canvas
        camera={{ position: [camTarget[0] - 4, camTarget[1] + 2.5, camTarget[2] - 4], fov: 58 }}
        gl={{ antialias: true }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[4, 8, 2]} intensity={0.65} />
        <pointLight position={[-2, 3, -2]} intensity={0.25} color="#2244ff" />

        <OrbitControls
          target={camTarget}
          enableDamping dampingFactor={0.07}
          minDistance={1.5} maxDistance={16}
        />

        {/* <RoomBox bounds={bounds} /> */}
        {/* <FloorGrid bounds={bounds} /> */}

        {activePreds.length > 0 && (
          <VoxelGrid
            predictions={activePreds}
            mode={mode}
            minVal={rawMin - pad}
            maxVal={rawMax + pad}
            opacity={opacity}
            bounds={bounds}
          />
        )}

        {Object.entries(SENSOR_POSITIONS).map(([name, pos]) => {
          const r = sensorReadings[name] || {};
          return (
            <SensorSphere
              key={name}
              name={name}
              roomPos={pos}
              temp={r.temp}
              humidity={r.humidity}
            />
          );
        })}
      </Canvas>

      <Legend mode={mode} minVal={rawMin} maxVal={rawMax} stats={stats} />
    </div>
    </div>
  );
};

export default Sensor3DView;